import { useState, useCallback } from 'react';
import type { AiSuggestOptions } from './aiPrompts.js';
import type { AskAtlasResult } from './atlasApiTypes.js';
import { validateOp, type SandboxOpEntry } from './sandboxOps.js';

export interface AiSuggestState {
  answer: string | null;
  fabricated: string[];
  busy: boolean;
  error: string | null;
}

const IDLE: AiSuggestState = { answer: null, fabricated: [], busy: false, error: null };

/**
 * Parse an AI-generated JSON string into SandboxOpEntry[] with proposed state.
 * Expected format: JSON array of op objects, each with optional "confidence" field (0-1).
 * Skips invalid ops. Returns [] on parse error or non-array JSON.
 */
export function parseAiOpsResponse(jsonString: string): SandboxOpEntry[] {
  try {
    const parsed = JSON.parse(jsonString);
    if (!Array.isArray(parsed)) return [];
    const result: SandboxOpEntry[] = [];
    for (const item of parsed) {
      if (typeof item !== 'object' || item === null) continue;
      const op = item as Record<string, unknown>;
      // Extract confidence before validation, so we don't lose it.
      const confidence = typeof op.confidence === 'number' && op.confidence >= 0 && op.confidence <= 1 ? op.confidence : undefined;
      // Validate the op (all fields except confidence).
      if (validateOp(item as any).ok) {
        result.push({
          op: item as any,
          state: 'proposed',
          confidence,
        });
      }
    }
    return result;
  } catch {
    return [];
  }
}

export function useAiSuggest(
  onAsk: (opts: AiSuggestOptions) => Promise<AskAtlasResult>,
) {
  const [state, setState] = useState<AiSuggestState>(IDLE);

  const suggest = useCallback(
    async (opts: AiSuggestOptions): Promise<AskAtlasResult | null> => {
      setState({ answer: null, fabricated: [], busy: true, error: null });
      try {
        const result = await onAsk(opts);
        setState({ answer: result.answer, fabricated: result.fabricated, busy: false, error: null });
        return result;
      } catch (e) {
        setState({
          answer: null,
          fabricated: [],
          busy: false,
          error: e instanceof Error ? e.message : 'Ask Atlas is unavailable right now.',
        });
        return null;
      }
    },
    [onAsk],
  );

  const clear = useCallback(() => setState(IDLE), []);

  return { ...state, suggest, clear };
}
